import './EditionPage.scss';
import { Icon, SimpleNumberCard } from '@design-system/index';
import { MembersListLine, Warning } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { MemberTable } from '@services/member/member/member.types';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate } from '@utils/dates';
import { useMemberEditionPageVM } from './useEditionPageVM';
import { List } from '@services/member/list/list.types';

export default function MemberEditionPage() {
    const {
        handleMetadata,
        columnHelper,
        stateCodes,
        totalMembers,
        useGetMembersEditionExportMutation,
        useGetMembersEditionMutation
    } = useMemberEditionPageVM();

    const columns = [
        columnHelper.accessor('cosemNumber', {
            header: 'N°COSEM',
            id: 'cosemNumber',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    {!info.row.original.contributionUpToDate && <Icon icon={<Warning />} size={18} color="error" />}
                    <>{info.getValue()}</>
                </div>
            )
        }),

        columnHelper.accessor((row) => row.administrativeInformation.registrationNumber, {
            id: 'administrativeInformation.registrationNumber',
            header: 'Matricule',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.administrativeInformation.registeredAt, {
            id: 'administrativeInformation.registeredAt',
            header: "Date d'adhésion",
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{formatDate(info.getValue())}</>
        }),
        columnHelper.accessor((row) => row.personalInformation.lastname, {
            id: 'personalInformation.lastname',
            header: 'Nom',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue().toUpperCase()}</>
        }),
        columnHelper.accessor((row) => row.personalInformation.firstname, {
            id: 'personalInformation.firstname',
            header: 'Prénom',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue().toUpperCase()}</>
        }),
        columnHelper.accessor((row) => row.isArchived, {
            id: 'isArchived',
            header: 'Archivé',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue() ? 'Oui' : 'Non'}</>
        })
    ] as ColumnDef<MemberTable>[];

    return (
        <div className="members-table-container">
            <div className="members-table-container-header">
                <div className="members-table-container-header-left">
                    <SimpleNumberCard labelForSingle={'Adhérent'} labelForMultiple={'Adhérents'} value={totalMembers} />
                </div>
            </div>
            <MutationTable
                key={'edition'}
                columns={columns}
                selectable={false}
                mutationHook={useGetMembersEditionMutation}
                defaultSorting={{
                    field: 'cosemNumber',
                    direction: 'desc'
                }}
                exportable={true}
                exportMutationHook={useGetMembersEditionExportMutation}
                headerTitle={'Édition adhérents'}
                headerIcon={<MembersListLine />}
                headerIconColor="green-water"
                onMetadata={handleMetadata}
                filters={[
                    {
                        id: 'administrativeInformation.registeredAt',
                        label: "Date d'adhésion",
                        type: 'date',
                        comparatorTypes: ['isBetween', 'isBefore', 'isAfter', 'equals']
                    },
                    {
                        id: 'administrativeInformation.stateCode',
                        label: 'Code état',
                        type: 'select',
                        options: stateCodes.map((state: List) => ({ label: state.label, value: state.code }))
                    },
                    {
                        id: 'hasContributionUpToDate',
                        label: 'Cotisation à jour',
                        type: 'boolean'
                    },
                    {
                        id: 'isArchived',
                        label: 'Archivé',
                        type: 'boolean'
                    }
                ]}
            />
        </div>
    );
}
